import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Column, Columns, } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useUpdateEffect } from 'react-use';
import { CategorizedListCategories } from './components/categories/categorizedListCategories';
import { CategorizedListMenu } from './components/menu/categorizedListMenu';
import { CategorizedListSearch } from './components/search/categorizedListSearch';
/**
 * Represent a component that presents a categorized list with an optional
 * search and bookmark menu.
 */
export const CategorizedList = ({ categories, selectedItems, asyncStatus, hasNextPage, showSearch = false, resetSearchOnInactive = false, showMenu = false, isMultiple = false, isActive = false, width = 300, height = 350, categoriesTopSlot, ItemContent, CreateItemContent, isItemFavorite, isItemDisabled, identityFn, nextPage, refetch, search, onChange, }) => {
    // Search state.
    const searchRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    // Categories state.
    const categoriesContainerRef = useRef(null);
    const [categoriesElement, setCategoriesElement] = useState(null);
    const [categoriesSectionElements, setCategoriesSectionElements] = useState({});
    const [visibleCategoryIds, setVisibleCategoryIds] = useState([]);
    const setTimeoutSearch = useCallback((searchTerm) => 
    // We delay this one frame to ensure that the search term updates at the
    // same time as the arg search term changes in the paginated hook as
    // these needs to stay in sync.
    setTimeout(() => {
        setSearchTerm(searchTerm);
    }), []);
    /** Handles changing the search. */
    const handleSearchChange = useMemo(() => debounce((searchTerm) => setTimeoutSearch(searchTerm), 300), [setTimeoutSearch]);
    /** Handles changing the menu item. */
    const handleMenuItemChange = useCallback(({ selectedItem: { value: category }, }) => {
        const categoryElement = categoriesSectionElements[category.id];
        if (categoryElement && categoriesContainerRef.current) {
            // For the top category `li` we subtract the border width (top + bottom)
            // of the categories `ul` list so that the scroll position is at zero.
            const categoriesOffset = category.id === categories[0].id ? 2 : 0;
            const top = categoryElement.offsetTop -
                categoriesContainerRef.current.offsetTop -
                categoriesOffset;
            categoriesContainerRef.current.scrollTo({ top, behavior: 'smooth' });
        }
    }, [categoriesSectionElements, categories]);
    /** Handles changing the category item. */
    const handleCategoryItemChange = useCallback((event) => {
        var _a;
        onChange === null || onChange === void 0 ? void 0 : onChange(event);
        (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [onChange]);
    // Handles setting the focus on either the search input or the categories
    // selection list when the categorized list becomes active.
    useEffect(() => {
        if (isActive) {
            // This has to be done in the next event loop frame through a timeout as
            // the DOM elements would otherwise not be ready.
            setTimeout(() => {
                if (searchRef.current) {
                    searchRef.current.focus();
                }
                else if (categoriesElement) {
                    categoriesElement.focus();
                }
            });
        }
    }, [categoriesElement, isActive]);
    // Handles performing the search across the list.
    useUpdateEffect(() => {
        if (showSearch) {
            search === null || search === void 0 ? void 0 : search(searchTerm, { debounceTime: 0 });
        }
    }, [searchTerm, showSearch, search]);
    // Handles resetting the search term when and if required.
    useEffect(() => {
        if (showSearch && !isActive && resetSearchOnInactive) {
            setTimeoutSearch('');
        }
    }, [isActive, resetSearchOnInactive, showSearch, setTimeoutSearch]);
    return (_jsxs(Columns, Object.assign({ style: { height } }, { children: [showMenu && (_jsx(Column, Object.assign({ size: "150px" }, { children: _jsx(Column.Row, Object.assign({ noHorizontalPadding: true, noVerticalPadding: true, noMinHeight: true, style: { padding: sizes.spacer[200] } }, { children: _jsx(CategorizedListMenu, { categories: categories, visibleCategoryIds: visibleCategoryIds, onChange: handleMenuItemChange }) })) }))), _jsxs(Column, Object.assign({ size: `${width}px` }, { children: [showSearch && (_jsx(Column.Row, Object.assign({ noHorizontalPadding: true, noVerticalPadding: true, noMinHeight: true, style: {
                            padding: sizes.spacer[200],
                            paddingBottom: 0,
                        } }, { children: _jsx(CategorizedListSearch, { ref: searchRef, value: searchTerm, setIsSearchFocused: setIsSearchFocused, onSearchChange: handleSearchChange }) }))), categoriesTopSlot && (_jsx(Column.Row, Object.assign({ noHorizontalPadding: true, noVerticalPadding: true, noMinHeight: true, style: { padding: sizes.spacer[200] } }, { children: categoriesTopSlot }))), _jsx(Column.Row, Object.assign({ noHorizontalPadding: true, noVerticalPadding: true, scrollable: true, ref: categoriesContainerRef }, { children: _jsx(CategorizedListCategories, { categories: categories, selectedItems: selectedItems, asyncStatus: asyncStatus, hasNextPage: hasNextPage, isMultiple: isMultiple, isActive: isSearchFocused, searchTerm: searchTerm, ItemContent: ItemContent, CreateItemContent: CreateItemContent, isItemFavorite: isItemFavorite, isItemDisabled: isItemDisabled, identityFn: identityFn, nextPage: nextPage, refetch: refetch, onChange: handleCategoryItemChange, setCategoriesElement: setCategoriesElement, setCategoriesSectionElements: setCategoriesSectionElements, setVisibleCategoryIds: setVisibleCategoryIds }) }))] }))] })));
};
